import { useState, useEffect, useCallback, useMemo } from "react";
import { fetchActivity } from "../utils/auth";

// Admin-only record of who changed what. Entries are written by the server —
// it diffs the incoming roster against the stored one on every save — so the
// log sees an edit whether or not the browser cooperates, and the actor is
// taken from the session rather than anything the client claims.
const ACTION_STYLES = {
  created: "bg-green-100 text-green-800",
  updated: "bg-blue-100 text-blue-800",
  deleted: "bg-red-100 text-red-700",
  "bulk-change": "bg-purple-100 text-purple-700",
  "signed-in": "bg-gray-100 text-gray-500",
};

function actionStyle(action) {
  if (ACTION_STYLES[action]) return ACTION_STYLES[action];
  if (action.startsWith("account-")) return "bg-indigo-100 text-indigo-700";
  return "bg-gray-100 text-gray-600";
}

// "Today", "Yesterday", then the date — so a week of work reads as a story
// rather than a wall of timestamps.
function dayLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  const today = new Date();
  const startOf = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(today) - startOf(d)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

function timeLabel(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function ActivityView() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [who, setWho] = useState("All");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      setRows(await fetchActivity(500));
      setError(null);
    } catch (e) {
      setRows([]);
      setError(e.message || "Could not load the activity log.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const actors = useMemo(
    () => [...new Set((rows || []).map(r => r.actor).filter(Boolean))].sort(),
    [rows]
  );

  const visible = useMemo(
    () => (rows || []).filter(r => who === "All" || r.actor === who),
    [rows, who]
  );

  // Server returns newest first; group runs of the same day under one heading.
  const grouped = useMemo(() => {
    const out = [];
    visible.forEach(row => {
      const label = dayLabel(row.at);
      if (!out.length || out[out.length - 1].label !== label) out.push({ label, rows: [] });
      out[out.length - 1].rows.push(row);
    });
    return out;
  }, [visible]);

  if (rows === null) {
    return <p className="text-sm text-gray-500 p-6">Loading activity…</p>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Activity</h2>
          <p className="text-sm text-gray-500">
            Every change, who made it, and what it was before. Newest first, last 500 entries.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={who}
            onChange={e => setWho(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="All">Everyone</option>
            {actors.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button
            onClick={load}
            className="text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          {error}
        </p>
      )}

      {visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <div className="text-3xl mb-2">🗒️</div>
          <p className="text-gray-500 text-sm">Nothing recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(group => (
            <div key={group.label}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                {group.label}
              </h3>
              <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                {group.rows.map(row => {
                  const hasDetail = (row.changes || []).length > 0;
                  const open = expanded === row.id;
                  return (
                    <div key={row.id} className="px-4 py-2.5">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <span className="text-xs text-gray-400 tabular-nums w-16 flex-shrink-0">
                          {timeLabel(row.at)}
                        </span>
                        <span className="text-sm font-medium text-gray-700">
                          {row.actorName || row.actor}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${actionStyle(row.action || "")}`}>
                          {row.action}
                        </span>
                        <span className="text-sm text-gray-600 flex-1 min-w-0">{row.summary}</span>
                        {hasDetail && (
                          <button
                            onClick={() => setExpanded(open ? null : row.id)}
                            className="text-xs text-blue-600 hover:text-blue-700 flex-shrink-0"
                          >
                            {open ? "Hide" : `${row.changes.length} field${row.changes.length === 1 ? "" : "s"}`}
                          </button>
                        )}
                      </div>

                      {open && (
                        <div className="mt-2 ml-16 overflow-x-auto">
                          <table className="text-xs text-gray-600 border-separate border-spacing-x-3">
                            <thead>
                              <tr className="text-gray-400">
                                <th className="text-left font-medium">Field</th>
                                <th className="text-left font-medium">Before</th>
                                <th className="text-left font-medium">After</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.changes.map((c, i) => (
                                <tr key={`${c.field}-${i}`}>
                                  <td className="font-mono whitespace-nowrap">{c.field}</td>
                                  <td className="text-red-600">{c.before}</td>
                                  <td className="text-green-700">{c.after}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
